// pages/TableOrdersPage.jsx — page STAFF (authentifiée) : gestion des tables
// + QR codes, et suivi en direct des commandes passées à distance.
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../utils/auth';
import api, { posTablesApi, tableOrdersStaffApi } from '../utils/api';
import { QrCode, Plus, Trash2, RefreshCw, Printer, AlertTriangle, CheckCheck,
  Clock, Receipt, ChefHat, Bell, XCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { format, isToday, isYesterday } from 'date-fns';
import { fr } from 'date-fns/locale';
import ConfirmModal from '../components/ConfirmModal';

const STATUS_FLOW = { paid:'preparing', preparing:'ready', ready:'served' };
const STATUS_LABEL = {
  pending_payment:'En attente de paiement', paid:'Reçue', preparing:'En préparation',
  ready:'Prête', served:'Servie', cancelled:'Annulée',
};
const NEXT_LABEL   = { paid:'Démarrer', preparing:'Marquer prête', ready:'Marquer servie' };
// Métadonnées unifiées par statut (icône + couleurs de badge) — utilisées à
// la fois sur les cartes "en cours" et sur les lignes de l'historique, pour
// un repérage visuel cohérent et plus rapide qu'un simple libellé texte.
const STATUS_META = {
  pending_payment: { icon: Clock,     bg:'rgba(245,166,35,0.15)', fg:'var(--amber)' },
  paid:            { icon: Receipt,   bg:'rgba(124,92,252,0.15)', fg:'var(--accent-light)' },
  preparing:       { icon: ChefHat,   bg:'rgba(124,92,252,0.15)', fg:'var(--accent-light)' },
  ready:           { icon: Bell,      bg:'rgba(124,92,252,0.15)', fg:'var(--accent-light)' },
  served:          { icon: CheckCheck,bg:'rgba(0,210,180,0.15)',  fg:'var(--teal)' },
  cancelled:       { icon: XCircle,   bg:'rgba(255,77,109,0.15)', fg:'var(--red)' },
};
function StatusBadge({ status }) {
  const meta = STATUS_META[status] || { icon: Clock, bg:'var(--bg-elevated)', fg:'var(--text-3)' };
  const Icon = meta.icon;
  return (
    <span style={{ display:'inline-flex', alignItems:'center', gap:4, fontSize:11, fontWeight:700,
      padding:'3px 9px', borderRadius:99, background:meta.bg, color:meta.fg, flexShrink:0 }}>
      <Icon size={11}/> {STATUS_LABEL[status]}
    </span>
  );
}
// Inclut l'unité (ex: "33cl") figée au moment de la commande — indispensable
// pour que le staff sache précisément quoi servir, à la validation ET dans
// l'historique, même si le produit change d'unité par la suite.
// Version texte brut : utilisée pour le title="" (tooltip au survol) et
// autres contextes qui ne peuvent pas afficher du JSX.
function itemLabel(it) {
  return `${it.qty}× ${it.name}${it.unit_label ? ` ${it.unit_label}` : ''}${it.variant_name ? ` (${it.variant_name})` : ''}`;
}
// Version riche : distingue visuellement la quantité/nom (gras) de l'unité
// (badge mono discret, ex: "33cl") et de la variante (italique) — au lieu de
// tout mélanger dans une seule chaîne de texte plate.
function ItemLine({ it }) {
  return (
    <>
      <span style={{ fontWeight:600 }}>{it.qty}× {it.name}</span>
      {it.unit_label && (
        <span style={{ marginLeft:5, fontSize:10.5, fontFamily:'monospace', color:'var(--text-3)',
          background:'var(--bg-elevated)', padding:'1px 6px', borderRadius:5, whiteSpace:'nowrap' }}>
          {it.unit_label}
        </span>
      )}
      {it.variant_name && (
        <span style={{ marginLeft:5, fontSize:11, fontStyle:'italic', color:'var(--text-3)' }}>
          ({it.variant_name})
        </span>
      )}
    </>
  );
}
function dayLabel(d) {
  if (isToday(d)) return "Aujourd'hui";
  if (isYesterday(d)) return 'Hier';
  return format(d, 'EEEE d MMMM', { locale: fr });
}

function TablesTab() {
  const [tables, setTables] = useState([]);
  const [newLabel, setNewLabel] = useState('');
  const [qrTable, setQrTable] = useState(null);
  const [qrDataUrl, setQrDataUrl] = useState(null);
  const [qrLoading, setQrLoading] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(null);
  const [removing, setRemoving] = useState(false);

  const load = useCallback(() => {
    posTablesApi.list().then(r => setTables(r.data.tables)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  // Le endpoint /pos-tables/:id/qr.png est authentifié (JWT en Authorization
  // header) — une balise <img src="..."> ne peut pas envoyer ce header, donc
  // on récupère l'image via un appel authentifié classique (axios) et on la
  // convertit en data URL pour l'affichage/l'impression.
  useEffect(() => {
    if (!qrTable) { setQrDataUrl(null); return; }
    let cancelled = false;
    setQrLoading(true);
    api.get(`/pos-tables/${qrTable.id}/qr.png`, { responseType: 'blob' })
      .then(r => {
        const reader = new FileReader();
        reader.onloadend = () => { if (!cancelled) setQrDataUrl(reader.result); };
        reader.readAsDataURL(r.data);
      })
      .catch(() => toast.error('Erreur de chargement du QR code'))
      .finally(() => !cancelled && setQrLoading(false));
    return () => { cancelled = true; };
  }, [qrTable]);

  const printQr = () => {
    if (!qrDataUrl) return;
    const w = window.open('', '_blank', 'width=400,height=500');
    w.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8">
      <style>body{font-family:system-ui,sans-serif;text-align:center;padding:30px}
        h1{font-size:18px;margin-bottom:16px}img{width:280px;height:280px}</style>
      </head><body><h1>${qrTable.label}</h1><img src="${qrDataUrl}"/></body></html>`);
    w.document.close();
    setTimeout(() => { w.focus(); w.print(); }, 300);
  };

  const createTable = async () => {
    if (!newLabel.trim()) return;
    try {
      await posTablesApi.create({ label: newLabel.trim() });
      setNewLabel(''); load();
      toast.success('Table créée');
    } catch { toast.error('Erreur'); }
  };
  const removeTable = async () => {
    setRemoving(true);
    try {
      await posTablesApi.remove(confirmRemove.id);
      load();
      setConfirmRemove(null);
    } catch { toast.error('Erreur'); }
    finally { setRemoving(false); }
  };

  return (
    <div>
      <div style={{ display:'flex', gap:8, marginBottom:16 }}>
        <input className="input" placeholder="Nom de la table (ex: Table 5, Terrasse 2)"
          value={newLabel} onChange={e=>setNewLabel(e.target.value)}
          onKeyDown={e => { if (e.key==='Enter') { e.stopPropagation(); createTable(); } }}/>
        <button className="btn btn-primary" onClick={createTable}><Plus size={14}/> Ajouter</button>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))', gap:12 }}>
        {tables.map(t => (
          <div key={t.id} className="card" style={{ padding:14 }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'start' }}>
              <div>
                <div style={{ fontWeight:700, fontSize:14 }}>{t.label}</div>
                <div style={{ fontSize:11, color:'var(--text-3)', marginTop:2 }}>
                  {t.is_active ? 'Active' : 'Désactivée'}
                </div>
              </div>
              <button onClick={() => setConfirmRemove(t)}
                style={{ background:'none', border:'none', cursor:'pointer', color:'var(--red)' }}>
                <Trash2 size={14}/>
              </button>
            </div>
            <button className="btn btn-secondary btn-sm" style={{ width:'100%', justifyContent:'center', marginTop:10 }}
              onClick={() => setQrTable(t)}>
              <QrCode size={13}/> Voir le QR code
            </button>
          </div>
        ))}
        {tables.length === 0 && (
          <div className="empty-state card"><p>Aucune table configurée</p></div>
        )}
      </div>

      {qrTable && (
        <div className="modal-overlay" onClick={e => e.target===e.currentTarget && setQrTable(null)}>
          <div className="modal" style={{ maxWidth:340, textAlign:'center' }}>
            <h3 style={{ marginTop:0 }}>{qrTable.label}</h3>
            {qrLoading ? (
              <div style={{ width:260, height:260, display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto' }}>
                <span className="spinner"/>
              </div>
            ) : qrDataUrl ? (
              <img src={qrDataUrl} alt="QR code" style={{ width:260, height:260, borderRadius:12 }}/>
            ) : (
              <div style={{ width:260, height:260, display:'flex', alignItems:'center', justifyContent:'center',
                margin:'0 auto', color:'var(--text-3)', fontSize:12 }}>
                QR code indisponible
              </div>
            )}
            <p style={{ fontSize:11, color:'var(--text-3)', wordBreak:'break-all', marginTop:8 }}>
              {qrTable.orderUrl}
            </p>
            <div style={{ display:'flex', gap:8, marginTop:12 }}>
              <button className="btn btn-secondary" style={{ flex:1, justifyContent:'center' }}
                disabled={!qrDataUrl} onClick={printQr}>
                <Printer size={13}/> Imprimer
              </button>
              <button className="btn btn-primary" style={{ flex:1, justifyContent:'center' }}
                onClick={() => setQrTable(null)}>
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmRemove && (
        <ConfirmModal
          title="Supprimer la table"
          message={`Supprimer "${confirmRemove.label}" ? Le QR code associé ne fonctionnera plus.`}
          confirmLabel="Supprimer"
          danger
          loading={removing}
          onConfirm={removeTable}
          onCancel={() => setConfirmRemove(null)}
        />
      )}
    </div>
  );
}

function OrdersTab() {
  const [orders, setOrders] = useState([]);
  const [confirmPay, setConfirmPay] = useState(null);
  const [confirmingPay, setConfirmingPay] = useState(false);
  const load = useCallback(() => {
    tableOrdersStaffApi.list().then(r => setOrders(r.data.orders)).catch(() => {});
  }, []);
  useEffect(() => {
    load();
    const t = setInterval(load, 15000); // rafraîchissement auto — nouvelles commandes en direct
    return () => clearInterval(t);
  }, [load]);

  const advance = async (order) => {
    const next = STATUS_FLOW[order.status];
    if (!next) return;
    await tableOrdersStaffApi.setStatus(order.id, next);
    load();
  };

  // Raccourci : passe directement à "Servie" sans repasser par chaque étape
  // intermédiaire — utile pour des commandes simples (boissons) qui n'ont pas
  // besoin d'un suivi de préparation détaillé.
  const serveNow = async (order) => {
    await tableOrdersStaffApi.setStatus(order.id, 'served');
    load();
  };

  // Validation manuelle : à utiliser quand le webhook Stripe ne remonte pas
  // (mode test préprod sans endpoint configuré) ou si le paiement a été
  // encaissé par un autre biais. Rejoue le décrément de stock + l'écriture
  // de caisse comme le ferait le webhook — ne vérifie PAS que l'argent a
  // réellement été reçu.
  const confirmPayment = async () => {
    setConfirmingPay(true);
    try {
      await tableOrdersStaffApi.confirmPayment(confirmPay.id);
      toast.success('Paiement validé');
      setConfirmPay(null);
      load();
    } catch(e) { toast.error(e.response?.data?.error || 'Erreur'); }
    finally { setConfirmingPay(false); }
  };

  const active = orders.filter(o => o.status !== 'served' && o.status !== 'cancelled');
  const past   = orders.filter(o => o.status === 'served' || o.status === 'cancelled');

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
        <span style={{ fontSize:13, color:'var(--text-3)' }}>Rafraîchi automatiquement toutes les 15s</span>
        <button className="btn btn-secondary btn-sm" onClick={load}><RefreshCw size={12}/> Actualiser</button>
      </div>

      <div style={{ fontSize:13, color:'var(--text-3)', fontWeight:700, textTransform:'uppercase',
        letterSpacing:'0.04em', marginBottom:10 }}>
        En cours {active.length > 0 && `(${active.length})`}
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(240px,1fr))', gap:14, marginBottom:32 }}>
        {active.map(o => {
          const pending = o.status === 'pending_payment';
          return (
            <div key={o.id} className="card" style={{ padding:16, boxShadow:'0 2px 10px rgba(0,0,0,0.18)',
              borderColor: pending ? 'var(--amber)' : o.status==='paid' ? 'var(--accent)' : 'var(--border)' }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
                <strong style={{ fontSize:15 }}>{o.table_label}</strong>
                <StatusBadge status={o.status}/>
              </div>
              <div style={{ display:'flex', flexDirection:'column', gap:4, fontSize:12,
                color:'var(--text-2)', marginBottom:10, lineHeight:1.5 }}>
                {o.items.map((it,i) => <div key={i}><ItemLine it={it}/></div>)}
              </div>
              {o.customer_note && (
                <div style={{ fontSize:11, color:'var(--amber)', marginBottom:10, fontStyle:'italic' }}>
                  « {o.customer_note} »
                </div>
              )}
              <div style={{ height:1, background:'var(--border)', margin:'0 0 10px' }}/>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', gap:6 }}>
                <span style={{ fontWeight:700, fontSize:16, color:'var(--accent-light)' }}>
                  {parseFloat(o.subtotal).toFixed(2)} €
                </span>
                {pending ? (
                  <button className="btn btn-sm" style={{ background:'var(--amber)', color:'#fff', gap:5 }}
                    onClick={() => setConfirmPay(o)} title="Le webhook Stripe ne remonte pas ? Validez manuellement.">
                    <AlertTriangle size={12}/> Valider paiement
                  </button>
                ) : (
                  <div style={{ display:'flex', gap:6 }}>
                    {/* Raccourci 1-clic : évite de repasser par chaque étape pour
                        une commande simple (ex: juste des boissons). */}
                    <button className="btn btn-secondary btn-sm" onClick={() => serveNow(o)} title="Marquer directement comme servie">
                      <CheckCheck size={12}/> Servir
                    </button>
                    {NEXT_LABEL[o.status] && (
                      <button className="btn btn-primary btn-sm" onClick={() => advance(o)}>
                        {NEXT_LABEL[o.status]}
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
        {active.length === 0 && (
          <div className="empty-state card"><p>Aucune commande en cours</p></div>
        )}
      </div>

      {past.length > 0 && (() => {
        // Groupé par jour (Aujourd'hui / Hier / date) pour rester lisible même
        // avec plusieurs jours d'historique mélangés.
        const recent = past.slice(0,25);
        const groups = [];
        for (const o of recent) {
          const d = new Date(o.paid_at || o.created_at);
          const key = d.toDateString();
          let g = groups.find(g => g.key === key);
          if (!g) { g = { key, date:d, orders:[] }; groups.push(g); }
          g.orders.push(o);
        }
        return (
          <div>
            <div style={{ fontSize:13, color:'var(--text-3)', fontWeight:700, textTransform:'uppercase',
              letterSpacing:'0.04em', marginBottom:12, paddingBottom:8, borderBottom:'1px solid var(--border)' }}>
              Historique récent
            </div>
            {groups.map(g => {
              // Total du jour = somme des commandes servies (les annulées ne comptent pas).
              const dayTotal = g.orders.filter(o => o.status !== 'cancelled')
                .reduce((sum, o) => sum + parseFloat(o.subtotal), 0);
              return (
                <div key={g.key} style={{ marginBottom:20 }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom:6 }}>
                    <span style={{ fontSize:11, color:'var(--text-3)', textTransform:'uppercase',
                      letterSpacing:'0.04em', fontWeight:700 }}>
                      {dayLabel(g.date)}
                    </span>
                    <span style={{ fontSize:12, color:'var(--text-2)', fontWeight:600 }}>
                      {g.orders.length} commande{g.orders.length > 1 ? 's' : ''} · {dayTotal.toFixed(2)} €
                    </span>
                  </div>
                  <div className="card" style={{ padding:0, overflow:'hidden' }}>
                    {g.orders.map((o,i) => {
                      const time = format(new Date(o.paid_at || o.created_at), 'HH:mm');
                      const itemsSummary = o.items.map(itemLabel).join(', ');
                      return (
                        <div key={o.id} title={itemsSummary} style={{ display:'flex', alignItems:'center', gap:10,
                          padding:'10px 14px', fontSize:12,
                          borderBottom: i < g.orders.length-1 ? '1px solid var(--border)' : 'none' }}>
                          <span style={{ color:'var(--text-3)', fontFamily:'monospace', flexShrink:0 }}>{time}</span>
                          <span style={{ fontWeight:600, flexShrink:0 }}>{o.table_label}</span>
                          <span style={{ flex:1, color:'var(--text-2)', overflow:'hidden',
                            textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                            {o.items.map((it,idx) => (
                              <span key={idx}>
                                <ItemLine it={it}/>{idx < o.items.length-1 ? ',  ' : ''}
                              </span>
                            ))}
                          </span>
                          <span style={{ fontWeight:700, flexShrink:0 }}>{parseFloat(o.subtotal).toFixed(2)} €</span>
                          <StatusBadge status={o.status}/>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        );
      })()}

      {confirmPay && (
        <ConfirmModal
          title="Valider le paiement manuellement"
          message={
            `Confirmer manuellement le paiement de ${parseFloat(confirmPay.subtotal).toFixed(2)} € pour ${confirmPay.table_label} ?\n\n` +
            `À utiliser uniquement si le paiement a réellement été reçu (ou en test préprod).`
          }
          confirmLabel="Valider"
          loading={confirmingPay}
          onConfirm={confirmPayment}
          onCancel={() => setConfirmPay(null)}
        />
      )}
    </div>
  );
}

// `embedded` : rendu comme onglet interne (ex: dans Mode caisse).
export default function TableOrdersPage({ embedded = false }) {
  const { can } = useAuth();
  const [tab, setTab] = useState('orders');

  if (!can('pos.access')) return (
    <div className="page-body"><div className="empty-state card"><p>Accès non autorisé</p></div></div>
  );

  return (
    <>
      {!embedded && (
        <div className="page-header">
          <div>
            <h2>Commandes à table</h2>
            <p>QR code + paiement CB à distance</p>
          </div>
        </div>
      )}
      <div className={embedded ? undefined : 'page-body'}>
        <div style={{ display:'flex', gap:8, marginBottom:16 }}>
          <button className={`btn ${tab==='orders'?'btn-primary':'btn-secondary'}`} onClick={()=>setTab('orders')}>Commandes</button>
          <button className={`btn ${tab==='tables'?'btn-primary':'btn-secondary'}`} onClick={()=>setTab('tables')}>Tables & QR codes</button>
        </div>
        {tab === 'orders' ? <OrdersTab/> : <TablesTab/>}
      </div>
    </>
  );
}
